import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api, buildWhatsAppLink } from '@/lib/api';
import SwipeGallery from '@/components/SwipeGallery';
import { ArrowUpRight, ArrowLeft } from 'lucide-react';

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        // fetch categories to resolve category + subcategory names
        api.get('/categories').then((cr) => {
          const cat = (cr.data || []).find((c) => c.slug === r.data.category);
          setCategory(cat || null);
        });
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="pt-40 px-6 text-center" data-testid="product-not-found">
        <p className="font-serif-display text-4xl">Product not found</p>
        <Link to="/products" className="mt-6 inline-block btn-outline">Back to collection</Link>
      </div>
    );
  }

  if (!product) return <div className="pt-40 text-center eyebrow" data-testid="product-detail-loading">Loading…</div>;

  const priceLabel = product.made_to_order && (!product.price || product.price === 0)
    ? 'On Enquiry'
    : `₹${Number(product.price).toLocaleString('en-IN')}`;

  const sub = category?.subcategories?.find((s) => s.slug === product.subcategory);
  const categoryLabel = category?.name || (product.category || '').replaceAll('-', ' ');

  return (
    <div className="pt-32 pb-24 px-6 sm:px-10 max-w-[1600px] mx-auto" data-testid={`product-detail-${product.id}`}>
      <Link to="/products" className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-[color:var(--mb-text-muted)] hover:text-[color:var(--mb-primary)] mb-10" style={{ transition: 'color 200ms ease' }}>
        <ArrowLeft size={14} /> Back to collection
      </Link>

      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
        >
          <div className="border border-[color:var(--mb-border)]">
            <SwipeGallery images={product.images} aspect="portrait" alt={product.name} />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="lg:col-span-5 lg:sticky lg:top-32 self-start"
        >
          <p className="eyebrow mb-3">
            {categoryLabel}{sub ? ` · ${sub.name}` : ''}{product.gender && product.gender !== 'unisex' ? ` · ${product.gender}` : ''}
          </p>
          <h1 className="font-serif-display text-4xl sm:text-5xl leading-[1] tracking-tight mb-6" data-testid="product-name">{product.name}</h1>
          <p className="font-serif-display text-2xl text-[color:var(--mb-primary)] mb-8" data-testid="product-price">{priceLabel}</p>

          <p className="text-[color:var(--mb-text-muted)] leading-relaxed font-light mb-8 whitespace-pre-line">{product.description}</p>

          <div className="border-t border-[color:var(--mb-border)] py-6 grid grid-cols-2 gap-6 text-sm">
            {product.fabric && (
              <div>
                <p className="eyebrow mb-2">Fabric</p>
                <p>{product.fabric}</p>
              </div>
            )}
            {product.colors?.length > 0 && (
              <div>
                <p className="eyebrow mb-2">Colours</p>
                <p>{product.colors.join(', ')}</p>
              </div>
            )}
            {product.sizes?.length > 0 && (
              <div className="col-span-2">
                <p className="eyebrow mb-2">Sizes</p>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((s) => (
                    <span key={s} className="px-3 py-1.5 border border-[color:var(--mb-border)] rounded-full text-xs tracking-widest">{s}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="col-span-2">
              <p className="eyebrow mb-2">Availability</p>
              <p className={product.in_stock ? 'text-[color:var(--mb-primary)]' : 'text-red-500'}>
                {product.made_to_order ? 'Made to Order · 2–6 weeks lead time' : product.in_stock ? 'In Stock' : 'Currently Unavailable'}
              </p>
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-4">
            <a
              href={buildWhatsAppLink(product.name)}
              target="_blank"
              rel="noreferrer"
              data-testid="whatsapp-enquire-btn"
              className="btn-red justify-center"
            >
              Enquire on WhatsApp
              <ArrowUpRight size={16} />
            </a>
            <Link to="/contact" data-testid="visit-store-btn" className="btn-outline justify-center">Visit our store</Link>
          </div>

          <p className="mt-8 text-xs text-[color:var(--mb-text-muted)] leading-relaxed">
            Every piece is handcrafted. Slight variations in weave, colour and finish are the marks of authentic Nepali craftsmanship.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
